/**
 * AGENT-BC: "Revenue & Quote Funnel" combo chart — left card.
 *
 * Reads `reportData.leftChart` (PBI visual `41b9171202744c6153a0`, sourced
 * via Agent BA's `_evaluate_visual` orchestrator). The series count and types
 * are NOT hardcoded — whatever PBI's combo visual emits is what we render.
 *
 * Y-axis heuristic (BC):
 *   - The FIRST series whose `type === "bar"` is treated as the currency
 *     series and mapped to the LEFT y-axis (formatted as $ / $K / $M).
 *   - Every other series (including additional bars and all lines) maps to
 *     the RIGHT y-axis as integer counts.
 *
 * Color heuristic (BC):
 *   - Bars cycle through `BAR_PALETTE` (series-1/2/4/6).
 *   - Lines cycle through `LINE_PALETTE_ORANGE` (series-3/5/7/8).
 *
 * Polish ships through `_chartUtils.ts` so all 4 bottom-strip charts share
 * a single PBI-fidelity look (rounded bar tops, line area gradient, rich
 * tooltip with formatted values, consistent margins).
 *
 * Anti-null x-axis defense lives in `useReportData.sanitizeXAxis`.
 */

import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { dashboardChartTheme } from "../tokens";
import {
  AXIS_LABEL_FONT_SIZE,
  buildComboSeries,
  cssVar,
  decorateComboSeries,
  formatCount,
  formatCurrencyAbbrev,
  makeComboTooltipFormatter,
  makeTooltipBlock,
  STANDARD_GRID,
  STANDARD_LEGEND,
  type SeriesDecoration,
} from "./_chartUtils";
import {
  sanitizeXAxis,
  useReportData,
  type ComboChartPayload,
  type ComboSeries,
} from "./useReportData";
import { useUiStore } from "../state/uiStore";

const CHART_ID = "revenueQuoteFunnel";
const FALLBACK_TITLE = "Revenue & Quote Funnel";

/**
 * SPEC-PINNED COLORS for charts 1 + 2.
 *
 * We intentionally bypass the shared `--dash-series-N` design tokens here.
 * The product spec for the Revenue / Revenue & Online Orders combo charts
 * pins three exact hex codes (blue bar, orange line, pink line) so the two
 * cards read as a matched pair regardless of palette token rotation in
 * `tokens.css`. If the design system tokens drift, these charts MUST keep
 * the spec colors — that's why the override lives inline, not in shared
 * utils.
 */
const SPEC_BAR_COLOR = "#1a73e8";          // Calculated Total — left axis bars
const SPEC_LINE_ISSUED_COLOR = "#ff8c42";  // IA_QuotesIssued_Dynamic — orange
const SPEC_LINE_CONVERTED_COLOR = "#e91e8c"; // IA_QuotesConverted_Dynamic — pink

/**
 * Apply spec-pinned colors on top of `decorateComboSeries`'s axis assignment.
 * Bars → blue. First line (Issued / Online Orders) → orange. Second line
 * (Converted) → pink. Any extra line series beyond two falls back to whatever
 * the shared decorator picked, so we don't crash the visual if the backend
 * ever emits a third line series.
 */
function applySpecColors(
  series: ComboSeries[],
  decorated: SeriesDecoration[],
): SeriesDecoration[] {
  let lineSeen = 0;
  return decorated.map((d, i) => {
    const s = series[i];
    if (s.type === "bar") {
      return { ...d, color: SPEC_BAR_COLOR };
    }
    lineSeen += 1;
    if (lineSeen === 1) return { ...d, color: SPEC_LINE_ISSUED_COLOR };
    if (lineSeen === 2) return { ...d, color: SPEC_LINE_CONVERTED_COLOR };
    return d;
  });
}

export function RevenueQuoteFunnelChart(): JSX.Element {
  const { data, isLoading, isError } = useReportData();
  const theme = useUiStore((s) => s.theme);

  const payload: ComboChartPayload | undefined = data?.leftChart;
  const xAxisData = sanitizeXAxis(payload?.xAxis, CHART_ID);
  const series: ComboSeries[] = payload?.series ?? [];
  const title = (payload?.title && payload.title.trim()) || FALLBACK_TITLE;

  // Empty-state guard.
  const hasData = xAxisData.length > 0 && series.length > 0;

  const option = useMemo(() => {
    if (!hasData) return null;

    const decorated = applySpecColors(series, decorateComboSeries(series));
    const mappedSeries = buildComboSeries(series, decorated);

    // Tooltip currency mapping: any series on the left axis (yAxisIndex 0) is
    // formatted as currency; the rest are formatted as counts.
    const currencyNames = new Set(
      series
        .filter((_, i) => decorated[i]?.yAxisIndex === 0)
        .map((s) => s.name),
    );

    // Axis-name labels — PBI shows the underlying measure on the left and
    // "Count" (or the verbatim line series name when there's only one) on
    // the right. We derive both from the live series so the chart reflects
    // whatever the visual emitted instead of hardcoding "Revenue".
    const leftSeries = series.filter((_, i) => decorated[i]?.yAxisIndex === 0);
    const rightSeries = series.filter((_, i) => decorated[i]?.yAxisIndex === 1);
    const leftName = leftSeries[0]?.name || "Revenue";
    const rightName =
      rightSeries.length === 1 ? rightSeries[0].name : "Count";

    return dashboardChartTheme({
      legend: STANDARD_LEGEND,
      grid: STANDARD_GRID,
      tooltip: makeTooltipBlock(makeComboTooltipFormatter(currencyNames)),
      xAxis: {
        type: "category",
        data: xAxisData,
        boundaryGap: true,
        axisLabel: { fontSize: AXIS_LABEL_FONT_SIZE },
      },
      yAxis: [
        {
          // Left axis — currency (the first bar-type series).
          type: "value",
          name: leftName,
          nameLocation: "middle" as const,
          nameGap: 48,
          nameTextStyle: {
            fontSize: AXIS_LABEL_FONT_SIZE,
            color: cssVar("--dash-chart-axis-label", "#5a6473"),
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: AXIS_LABEL_FONT_SIZE,
            formatter: (v: number) => formatCurrencyAbbrev(v),
          },
          splitLine: { show: true, lineStyle: { type: "dotted" as const } },
        },
        {
          // Right axis — counts.
          type: "value",
          name: rightName,
          nameLocation: "middle" as const,
          nameGap: 40,
          nameTextStyle: {
            fontSize: AXIS_LABEL_FONT_SIZE,
            color: cssVar("--dash-chart-axis-label", "#5a6473"),
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: AXIS_LABEL_FONT_SIZE,
            formatter: (v: number) => formatCount(v),
          },
          splitLine: { show: false },
        },
      ],
      series: mappedSeries,
    });
  }, [hasData, series, xAxisData, theme]);

  return (
    <div className="dash-card">
      <h3 className="charts-row__title">{title}</h3>
      {isLoading ? (
        <div className="charts-row__empty charts-row__empty--shimmer" aria-busy="true">
          <span className="charts-row__shimmer" />
        </div>
      ) : isError ? (
        <div className="charts-row__empty charts-row__empty--message">Failed to load chart data</div>
      ) : !hasData || !option ? (
        <div className="charts-row__empty charts-row__empty--message">No chart data available</div>
      ) : (
        <ReactECharts
          className="charts-row__chart"
          option={option}
          notMerge
          lazyUpdate
          style={{ height: "100%", width: "100%" }}
        />
      )}
    </div>
  );
}

export default RevenueQuoteFunnelChart;
